import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  ChangeDetectorRef,
  OnDestroy,
  AfterViewInit,
  inject,
  Signal,
  Inject,
  PLATFORM_ID,
  InjectionToken,
  ViewChild,
  ElementRef,
  effect,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, combineLatest, debounceTime, filter, map, Observable, of, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import { Cart, Customer, ShopifyError } from '../../../shop/models';

import * as fromShopify from '../../../shop';
import { Line, Merchandise } from 'src/app/shop/models/cart.model';
import { getCustomer } from '../../../shop';
import { SetChatStatus } from 'src/app/store';
import { Location, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { FromDictionaryPipe, LanguageService } from '@teamfoster/sdk/dictionary-ngrx';
import { FormControl, Validators } from '@angular/forms';
import { CookiePreferences, getCookiePreferences } from '@teamfoster/sdk/cookie-ngrx';

@Component({
  selector: 'app-cart-overview',
  templateUrl: './cart-overview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CartOverviewComponent implements OnInit, OnDestroy, AfterViewInit {
  private store = inject(Store);
  private cd = inject(ChangeDetectorRef);
  private lang = inject(LanguageService);

  @ViewChild('stickycart') stickyCartElement?: ElementRef<HTMLElement>;

  prefix: string = 'winkelwagen-overzicht';
  inviewPort = false;
  products: any;
  delivery = 9;
  btw = 0.21;
  currencyCode = 'EUR';

  cookiePrefs$ = this.store.selectSignal(getCookiePreferences);

  cart$ = this.store.selectSignal(fromShopify.getCart);
  cartLoading$ = this.store.selectSignal(fromShopify.getCartLoading);
  cartLinesLoading$ = this.store.selectSignal(fromShopify.getCartLinesLoading);
  customer$ = this.store.selectSignal(getCustomer) as Signal<Customer>;
  cartErrors$ = this.store.selectSignal(fromShopify.getCartErrors);
  customerHasVat$ = this.store.selectSignal(fromShopify.getCustomerHasVatNumber);
  customerLoaded$ = this.store.selectSignal(fromShopify.getCustomerLoaded);
  discountLoaded$ = this.store.selectSignal(fromShopify.getLoadedDiscountUpdate);
  discountLoading$ = this.store.selectSignal(fromShopify.getLoadingDiscountUpdate);

  exemptionsChecking$ = this.store.selectSignal(fromShopify.getExemptionChecking);
  exemptionsChecked$ = this.store.selectSignal(fromShopify.getExemptionChecked);
  exemptionsValid$ = this.store.selectSignal(fromShopify.getExemptionValid);

  updateAlert = effect(() => {
    const checked = this.exemptionsChecked$();

    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        this.updateCartHeight();
      }, 100);
    }
  });

  isSignedIn$!: Observable<boolean>;
  private _unsubscribe$ = new Subject<void>();

  initDiscountPanelOpen = false;
  location = inject(Location);
  router = inject(Router);

  private dict = new FromDictionaryPipe(this.lang);

  noteField = new FormControl('', [Validators.maxLength(5000)]);

  constructor(@Inject(PLATFORM_ID) private platformId: InjectionToken<Object>) {}

  ngOnInit(): void {
    if (this.cart$()) {
      this.noteField.setValue(this.cart$()?.note || '', { emitEvent: false });
      this.initDiscountPanelOpen = !this.cart$()?.discountCodes.length;
    }

    this.isSignedIn$ = this.checkStore().pipe(
      tap(a => this.cd.detectChanges()),
      switchMap(() => of(true)),
      catchError(() => of(false))
    );

    this.isSignedIn$.pipe(takeUntil(this._unsubscribe$)).subscribe();

    this.noteField.valueChanges.pipe(takeUntil(this._unsubscribe$), debounceTime(200)).subscribe(note => {
      this.updateCartNote(note || '');
    });
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        this.store.dispatch(SetChatStatus({ status: 'sticky-cart-bottom' }));
        this.updateCartHeight();
      }, 100);
    }
  }

  updateCartHeight() {
    // set
    if (isPlatformBrowser(this.platformId)) {
      const height = this.stickyCartElement?.nativeElement.offsetHeight;
      document.documentElement.style.setProperty('--sticky-cart-height', height + 'px');
      this.cd.detectChanges();
    }
  }

  trackByFn(index: number, item: Line) {
    return item.id;
  }

  inViewChange(e: boolean) {
    this.inviewPort = e;
    this.store.dispatch(SetChatStatus({ status: e ? 'default' : 'sticky-cart-bottom' }));
  }

  // Get customer data
  checkStore(): Observable<boolean> {
    return combineLatest([
      this.store.select(fromShopify.getCustomerLoading),
      this.store.select(fromShopify.getTokenInitialized),
      this.store.select(fromShopify.getCustomerAccessToken),
    ]).pipe(
      tap(([loading, initialized, token]) => {
        if (!this.customerLoaded$() && !loading && initialized && token) {
          this.store.dispatch(fromShopify.LoadCustomer());
        }
      }),
      map(([loaded]) => loaded),
      filter(loaded => loaded),
      take(1)
    );
  }

  ngOnDestroy(): void {
    this.store.dispatch(SetChatStatus({ status: 'default' }));
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  checkout(force: boolean = false) {
    if (this.customerHasVat$() && this.customer$() && !force) {
      this.store.dispatch(fromShopify.CheckTaxExemptions());
      return;
    }
    this.store.dispatch(fromShopify.StartCheckout());
  }

  goBack(): void {
    const navigationId = (this.location.getState() as any)?.navigationId || 0;
    if (navigationId > 1) {
      this.location.back();
    } else {
      this.router.navigate([this.dict.transform('winkelwagen-verder-winkelen-url', false) || '/']);
    }
  }

  updateCartNote(note: string) {
    // console.log(note);
    this.store.dispatch(fromShopify.UpdateCartNote({ note }));
  }
}
